import { Audit } from '@/modules/audit/models/Audit';
import { DateFormat } from '@/modules/core/app/constants/date.constants';
import { BaseEntityModel } from '@/modules/core/app/models/abstracts/BaseEntityModel';
import { DayJsDate } from '@/modules/core/app/models/casts/DayJsDate';
import { EstateType } from '@/modules/estate/enums/EstateType';
import { EditorTemplateCategory } from '@/modules/export/_modules/editor/constants/EditorTemplateCategory';
import { EditorTemplateCommentThread } from '@/modules/export/_modules/editor/models/EditorTemplateCommentThread';
import { EditorTemplateDocument } from '@/modules/export/_modules/editor/models/EditorTemplateDocument';
import { User } from '@/modules/user/models/User';
import dayjs from 'dayjs';

export class EditorTemplate extends BaseEntityModel {
  id?: number;
  name?: string;
  category: EditorTemplateCategory;
  contents: string;
  type: string;
  locale?: string;
  jurisdictionId?: number;
  countryId?: number;
  estateType?: EstateType;
  editorTemplateCommentThreads?: Relationship<EditorTemplateCommentThread[]> | null;
  editorUserId?: number;
  editorUser?: Relationship<User> | null;
  lastEditedContentsAuditByUser?: Relationship<Audit> | null;
  createdAuditByUser?: Relationship<Audit> | null;
  override?: boolean;
  editorTemplateDocument?: Relationship<EditorTemplateDocument> | null;
  lastRegeneratedByUserId?: number;
  lastRegeneratedAt?: string;
  lastRegeneratedByUser?: Relationship<User> | null;
  lastOpenedAt?: string;
  dayjsLastOpenedAt: DayJsDate | null;

  constructor(model: Partial<EditorTemplate> = {}) {
    super(model);

    this.id = model.id;
    this.name = model.name;
    this.type = model.type!;
    this.category = model.category as EditorTemplateCategory;
    this.contents = model.contents!;
    this.jurisdictionId = model.jurisdictionId;
    this.locale = model.locale;
    this.countryId = model.countryId;
    this.editorUserId = model.editorUserId;
    this.estateType = model.estateType as EstateType;
    this.lastRegeneratedByUserId = model.lastRegeneratedByUserId;
    this.lastRegeneratedAt = model.lastRegeneratedAt;
    this.lastOpenedAt = model.lastOpenedAt;
    this.dayjsLastOpenedAt = model.lastOpenedAt ? new DayJsDate(model.lastOpenedAt) : null;
    this.editorTemplateCommentThreads = EditorTemplateCommentThread.mapModelsOrNull(
      model.editorTemplateCommentThreads
    );
    this.editorUser = User.mapModelOrNull(model.editorUser);
    this.lastEditedContentsAuditByUser = Audit.mapModelOrNull(model.lastEditedContentsAuditByUser);
    this.createdAuditByUser = Audit.mapModelOrNull(model.createdAuditByUser);
    this.editorTemplateDocument = EditorTemplateDocument.mapModelOrNull(
      model.editorTemplateDocument
    );
    this.lastRegeneratedByUser = User.mapModelOrNull(model.lastRegeneratedByUser);
  }

  canEdit(user: User): boolean {
    if (!this.editorUserId || this.dayjsLastOpenedAt?.isMoreThan8Hours()) {
      return true;
    }

    return this.editorUserId === user.id;
  }

  get formattedLastRegeneratedAt(): string {
    return dayjs(this.lastRegeneratedAt).format(DateFormat.FULL_MONTH_DAY_YEAR_WITH_TWELVE_HOUR);
  }

  get isoFormattedLastRegeneratedAt(): string {
    return dayjs(this.lastRegeneratedAt).format(DateFormat.ISO);
  }
}
